import {timezones} from 'shared/lib/timezones';

import React from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {SubmitHandler, useForm} from 'react-hook-form';
import {toast} from 'react-toastify';
import {t} from '@lingui/core/macro';

import Select from 'shared/ui/Select/Select';
import FormGroup from 'shared/ui/FormGroup/FormGroup';
import EmailInput from 'shared/ui/Input/InputEmail';
import Button from 'shared/ui/Button/Button/Button';
import FormGroupWithError from 'shared/ui/FormGroup/FormGroupWithError';
import InputName from 'shared/ui/Input/InputName';
import {IProfileForm, IUserOutput, editUser, isUserEditingSelector} from 'entities/user';
import {TypedDispatch} from 'shared/model/types';
import {getClientDefaultTimeZone} from 'shared/lib/datetime';

import styles from './Profile.module.scss';

interface IProfileFormProps {
    user: IUserOutput;
    onDeleteUser: () => void;
}

const ProfileForm: React.FC<IProfileFormProps> = ({user, onDeleteUser}) => {
    const {register, handleSubmit, formState, control, watch} = useForm<IProfileForm>({
        mode: 'onChange',
        defaultValues: {
            first_name: user.first_name || '',
            last_name: user.last_name || '',
            timezone: user?.timezone ?? getClientDefaultTimeZone(),
            email: user.email || '',
        },
    });

    const isEditing = useSelector(isUserEditingSelector);
    const dispatch = useDispatch<TypedDispatch>();
    const {errors, isValid, isDirty} = formState;

    const firstNameValue = watch('first_name', user.first_name);
    const lastNameValue = watch('last_name', user.last_name);

    const nameValidationRules = {
        required: t`Обязательное поле`,
        maxLength: {
            value: 64,
            message: t`Максимальная длина — 64 символа`,
        },
        pattern: {
            value: /.*\S+.*/,
            message: t`Поле не может состоять только из пробелов`,
        },
    };

    const onSubmit: SubmitHandler<IProfileForm> = async({timezone, ...rest}) => {
        try {
            await dispatch(editUser({
                id: user?.id ?? 0,
                timezone: timezone ?? getClientDefaultTimeZone(),
                ...rest,
            })).unwrap();

            toast.info(t`Данные сохранены`);
        } catch (e) {
        }
    };

    return (
        <form
            onSubmit={handleSubmit(onSubmit)}
            className="profile-form"
        >
            <FormGroupWithError
                error={errors?.first_name?.message}
                label={t`Имя`}
                className={styles.form_group}
            >
                <div className={styles.inputWrapper}>
                    <InputName
                        name="first_name"
                        max={64}
                        placeholder={t`Имя`}
                        control={control}
                        register={register}
                        rules={nameValidationRules}
                        required
                    />
                    <div className={styles.charCounter}>
                        {firstNameValue?.length || 0} / 64
                    </div>
                </div>
            </FormGroupWithError>

            <FormGroupWithError
                error={errors?.last_name?.message}
                label={t`Фамилия`}
                className={styles.form_group}
            >
                <div className={styles.inputWrapper}>
                    <InputName
                        name="last_name"
                        placeholder={t`Фамилия`}
                        max={64}
                        control={control}
                        register={register}
                        rules={nameValidationRules}
                        required
                    />
                    <div className={styles.charCounter}>
                        {lastNameValue?.length || 0}/64
                    </div>
                </div>
            </FormGroupWithError>

            <FormGroupWithError
                error={errors?.timezone?.message}
                label={t`Часовой пояс`}
                className={styles.form_group}
            >
                <Select
                    control={control}
                    name="timezone"
                    data={timezones.map(zone => ({value: zone, label: zone}))}
                />
            </FormGroupWithError>

            <FormGroup className={styles.form_group}>
                <EmailInput
                    register={register}
                    control={control}
                    className={styles.form_group}
                    disabled
                />
            </FormGroup>

            <div className={styles.buttonsBlock}>
                <Button
                    type="submit"
                    loading={isEditing}
                    className={styles.button}
                    disabled={!isDirty || !isValid || isEditing}
                >
                    {t`Сохранить`}
                </Button>
                <button
                    className={styles.deleteButton}
                    onClick={onDeleteUser}
                    type="button"
                >
                    {t`Удалить аккаунт`}
                </button>
            </div>

        </form>
    );
};

export default ProfileForm;